import { useInfiniteQuery } from "hooks/useInfiniteQuery";
import { PAGE_SIZE } from "utils/constants";
import { deduplicateBy } from "utils";
import { getMyStrategies } from "../service";

export const useMyStrategies = ({
  search = "",
  sort = "",
  performancePeriod = "all time",
  enabled = true,
} = {}) => {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["strategies", "my_strategies", search, sort, performancePeriod],
    enabled,
    queryFn: async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam,
        size: PAGE_SIZE,
        search,
        sort,
      };

      const response = await getMyStrategies(params);

      const processedData = response.data.map((strategy) => {
        const transformedStrategy = { ...strategy };

        if (
          transformedStrategy.performance &&
          typeof transformedStrategy.performance === "object"
        ) {
          transformedStrategy.performance =
            transformedStrategy.performance[performancePeriod] || 0;
        } else {
          transformedStrategy.performance = 0;
        }

        return transformedStrategy;
      });

      return {
        data: processedData,
        currentPage: Number(response.page ?? (pageParam || 1)),
        totalPages: Number(response.total_pages ?? 1),
        totalCount: Number(response.total_count ?? 0),
      };
    },
    getNextPageParam: (lastPage) => {
      const currentPage = Number(lastPage.currentPage);
      const totalPages = Number(lastPage.totalPages);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });

  const allStrategies = deduplicateBy(
    data?.pages?.flatMap((page) => page.data) || [],
    (s) => s.strategy_id
  );
  const latestPage = data?.pages?.[data.pages.length - 1];
  const totalPages = latestPage?.totalPages || 0;
  const totalCount = latestPage?.totalCount || 0;
  const currentPage = latestPage?.currentPage || 1;

  return {
    strategies: allStrategies,
    totalPages,
    totalCount,
    currentPage,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    fetchNextPage,
  };
};
